import config from '../config';

const API_BASE_URL = config.apiUrl;

// Helper pour gérer les erreurs d'authentification
const handleAuthError = (response) => {
    if (response.status === 401) {
        // Token expiré ou invalide - soft logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
};

export const authService = {
    async login(email, password) {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Login failed');
        }

        const data = await response.json();
        return data.data;
    },

    async register(email, password, firstName, lastName) {
        const response = await fetch(`${API_BASE_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email,
                password,
                first_name: firstName,
                last_name: lastName
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Registration failed');
        }

        const data = await response.json();
        return data.data;
    },

    async logout() {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
            try {
                await fetch(`${API_BASE_URL}/auth/logout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refresh_token: refreshToken })
                });
            } catch (error) {
                console.error('Logout error:', error);
            }
        }
    },

    async refreshToken() {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        const data = await response.json();
        localStorage.setItem('access_token', data.data.access_token);
        return data.data.access_token;
    },

    async getProfile() {
        const token = localStorage.getItem('access_token');
        if (!token) {
            throw new Error('No access token');
        }

        const response = await fetch(`${API_BASE_URL}/user/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
            if (response.status === 401) {
                // Token expiré, essayer de rafraîchir
                try {
                    const newToken = await this.refreshToken();
                    // Réessayer avec le nouveau token
                    const retryResponse = await fetch(`${API_BASE_URL}/user/me`, {
                        headers: { 'Authorization': `Bearer ${newToken}` }
                    });
                    const retryData = await retryResponse.json();
                    return retryData.data.user;
                } catch (error) {
                    throw new Error('Session expired');
                }
            }
            throw new Error('Failed to fetch profile');
        }

        const data = await response.json();
        return data.data.user;
    },

    async updateProfile(firstName, lastName) {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/user/profile`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                first_name: firstName,
                last_name: lastName
            })
        });

        if (!response.ok) {
            handleAuthError(response);
            const error = await response.json();
            throw new Error(error.error || 'Update failed');
        }

        return await response.json();
    },

    async changePassword(currentPassword, newPassword) {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/user/password`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                current_password: currentPassword,
                new_password: newPassword
            })
        });

        if (!response.ok) {
            handleAuthError(response);
            const error = await response.json();
            throw new Error(error.error || 'Password change failed');
        }

        return await response.json();
    }
};
